import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';

import {
  decisionDraftDelete,
  decisionRemoveVisibleModal,
  decisionSetOnSaveButtonClick,
  decisionSignSubmitFail,
  decisionSignUploadModalApply,
} from '../actions/decisionActions';
import { IDecisionFormRef } from '../interfaces/IDecisionFormRef';
import { knmRemoveVisibleModal } from '../../../KnmPackage/MainModule/actions/knmActions';

interface IUseDecisionFormRenderProps {
  formRef: IDecisionFormRef;
  decisionRawData?: IDecision;
  handleClearDraftPassportData: () => void;
}

export function useDecisionFormRender({
  formRef,
  decisionRawData,
  handleClearDraftPassportData,
}: IUseDecisionFormRenderProps) {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const setOnSaveButtonClick: (onSaveButtonClick: () => void) => void = useCallback(
    (onSaveButtonClick: () => void) => dispatch(decisionSetOnSaveButtonClick(onSaveButtonClick)),
    [dispatch],
  );

  const closeSignModal = () => dispatch(decisionRemoveVisibleModal(KnmModalsEnum.SIGN_MODAL));

  const closeSignUploadModal = () => dispatch(decisionRemoveVisibleModal(KnmModalsEnum.SIGN_UPLOAD_MODAL));

  const handleClosePrescribeCancelModal = () =>
    dispatch(decisionRemoveVisibleModal(KnmModalsEnum.ERKNM_PRESCRIBE_CANCEL));

  const handleCloseSignAfterCancelModal = () =>
    dispatch(decisionRemoveVisibleModal(KnmModalsEnum.ERKNM_SIGN_AFTER_CANCEL));

  const handleCloseActCancelModal = () => dispatch(decisionRemoveVisibleModal(KnmModalsEnum.ERKNM_ACT_CANCEL));

  const handleSignUploadModalApply = () => {
    dispatch(decisionSignUploadModalApply());
  };

  const deleteAutosavedDraft = useCallback(() => {
    if (decisionRawData) {
      dispatch(decisionDraftDelete(decisionRawData.id, handleClearDraftPassportData));
    }
  }, [decisionRawData, handleClearDraftPassportData]);

  const handleSaveButtonClick = useCallback(() => {
    formRef.current.submit();
    const { errors: currentErrors, hasValidationErrors: isValidationErrors, values } = formRef.current.getState();

    const isDraft = values?.draft;

    if (!isValidationErrors && decisionRawData?.id && isDraft) {
      deleteAutosavedDraft();
    }

    isValidationErrors && dispatch(decisionSignSubmitFail(currentErrors));
  }, [dispatch, formRef, decisionRawData]);

  const handleCloseErknmLinkOtherWarningModal = () =>
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_LINK_OTHER_WARNING_MODAL));

  useEffect(() => {
    setOnSaveButtonClick(handleSaveButtonClick);
  }, [handleSaveButtonClick, setOnSaveButtonClick]);

  return {
    closeSignModal,
    closeSignUploadModal,
    handleSignUploadModalApply,
    handleClosePrescribeCancelModal,
    handleCloseActCancelModal,
    handleCloseSignAfterCancelModal,
    handleCloseErknmLinkOtherWarningModal,
  };
}
